<template>
  <!-- eslint-disable max-len -->
  <main class="u-container">
    <form
      ref="form"
      novalidate
      @submit.prevent="validate"
    >
      <section>
        <info-panel
          id="ivs-locked-out-banner"
          :display-panel="mapMfaErrors('VerificationLockedException', 'PasscodeLockedException', 'SmfaLockedException')"
          panel-type="info"
          info-type="info"
        >
          <strong>
            There have been too many verification attempts. If you would like
            to proceed, please select and follow one of the options below:
          </strong>
          <ul>
            <li>Complete your change of address at a later time: {{ this.$store.get('mfa/error').message }}.</li>
          </ul>
          <p>OR</p>
          <ul>
            <li>
              Continue entering your information here. Once complete, visit your
              local Post Office to validate your identity and activate your request.
            </li>
          </ul>
        </info-panel>
        <info-panel
          id="ivs-system-error-banner"
          :display-panel="mapMfaErrors('SystemErrorException', 'UnverifiedPhoneException', 'IvsValidationException')"
          panel-type="info"
          info-type="info"
        >
          <strong>We are experiencing a system error. If you would like to proceed,
            please select and follow one of the options below:</strong>
          <ul>
            <li>Complete your online change of address at a later time.</li>
          </ul>
          <p>OR</p>
          <ul>
            <li>
              Continue entering your information here. Once complete, visit
              your local Post Office to validate your identity and activate your request.
            </li>
          </ul>
        </info-panel>
        <info-panel
          id="ivs-timeout-banner"
          :display-panel="mfaError('SmfaTimeoutException')"
          panel-type="info"
          info-type="info"
        >
          <strong>
            The transaction has timed out. If you would like to proceed,
            please select and follow one of the options below:
          </strong>
          <ul>
            <li>Complete your online change of address at a later time.</li>
          </ul>
          <p>OR</p>
          <ul>
            <li>
              Continue entering your information here. Once complete, visit
              your local Post Office to validate your identity and activate your request.
            </li>
          </ul>
        </info-panel>
        <h1 class="font-bold">
          USPS Change-of-Address<sup>®</sup>
        </h1>
        <IndividualProgressBarWithSteps
          v-if="showSimplifyExperience"
          :mobile="false"
        />
        <IndividualProgressBar
          v-if="!showSimplifyExperience"
          role="progressbar"
          :aria-valuenow="progressStep"
          :aria-valuemin="0"
          :aria-valuemax="1"
        />
        <!-- Banner Messages -->
        <div
          v-if="movingFromPoBox"
          ref="bannerOldPoBox"
          class="bg-gray-100 mt-5 py-4 px-6"
        >
          <p>
            <strong>You are changing your address from a Post Office Box.</strong> Our address database has recognized your old address as a post office box.
            <template v-if="moverType === 'BUSINESS'">
              Please note that the USPS policy only allows the boxholder customer or authorized representative of the organization listed on <i>PS Form 1093, Application for Post Office Box Service</i> to file Change-of-Address (COA) orders. Forwarding of mail for other persons receiving mail at the box is the responsibility of the organization.
            </template>
            <template v-else>
              Please note that the USPS policy only allows the box customer who signs <i>PS Form 1093, Application for Post Office Box Service</i> to file Change-of-Address orders. Forwarding of mail for other persons receiving mail at the box is the responsibility of the official boxholder.
            </template>
          </p>
        </div>
        <div
          v-if="newAddressDpo"
          ref="bannerNewDpo"
          class="bg-gray-100 mt-5 py-4 px-6"
        >
          <p>
            <strong>You are changing your address to a Diplomatic Post Office address.</strong> Our address database has recognized your new address as a Diplomatic Post Office address, and we will forward your mail as requested.
          </p>
        </div>
        <div
          v-if="newAddressCmra"
          ref="bannerNewCmra"
          class="bg-gray-100 mt-5 py-4 px-6"
        >
          <p>
            <strong>You are changing your address to a Private Mail Box.</strong> Our address database has recognized your new address as a Private Mail Box held at a Commercial Mail Receiving Agency (CMRA), and we will forward your mail as requested. Please note that the USPS policy does not support forwarding mail out of CMRAs. This means if you change your address again in the future, you must contact your Commercial Mail Receiving Agency to forward your mail.
          </p>
        </div>
        <div
          v-if="newAddressInUniqueZip"
          ref="bannerNewUniqueZip"
          class="bg-gray-100 mt-5 py-4 px-6"
        >
          <p>
            <strong>Our address database has recognized your new address as a unique address</strong>, and we will forward your mail as requested. Please note that the USPS policy does not support forwarding mail from a unique address; if you change your address again in the future, you must contact the mail center for this business or institution to forward your mail.
          </p>
        </div>
        <div
          v-if="newAddressBusiness && moverType !== 'BUSINESS'"
          ref="bannerResToBiz"
          class="bg-gray-100 mt-5 py-4 px-6"
        >
          <p>
            <strong>You are changing your address from a residential address to a business address.</strong> Our address database has recognized your new address as a business or organization (e.g., corporations, universities, hospitals, hotels), and we will forward your mail as requested. Please note that the USPS policy does not allow individual or family movers to forward mail from a business/organization address. This means if you change your address again in the future, the business/organization will be responsible for forwarding your mail.
          </p>
        </div>
        <!-- Verify Identity Section -->
        <div class="flex mt-10">
          <div class="w-1/2 max:pr-xl">
            <h2>
              <template v-if="!isDesktop && !showChoiceExperience">
                Step 3:&nbsp;
              </template>
              <span class="font-bold">Verify identity</span>
            </h2>
            <template v-if="businessToMilitary">
              <p class="mt-4">
                <strong>Billing address</strong>
              </p>
              <p class="mt-2">
                To verify your identity, the address on file with your credit card company must match your old address.
              </p>
              <p
                ref="bizToMil"
                class="mt-6"
              >
                {{ hrMcoaAddress.line1 }}<br>
                {{ hrMcoaAddress.line2 }}
              </p>
              <div class="c-hairline mt-6" />
              <p class="mt-4">
                We'll ask for the rest of your credit or debit card information on the next page.
              </p>
            </template>
            <fieldset v-else>
              <legend class="sr-only">
                Select billing address
              </legend>
              <p class="mt-4">
                <strong>Select billing address</strong>
              </p>
              <p>To verify your identity, the address on file with your credit card company must match the address you select.</p>
              <InputRadio
                ref="oldBillingType"
                v-model="billingAddressType"
                form-value="OLD"
                name="billing-address"
                class="c-input-radio mt-6"
                @input="analytics.click.oldBillingType"
              >
                <template v-if="!showChoiceExperience">
                  {{ hrOldAddress }}
                </template>
                <template v-if="showChoiceExperience">
                  {{ mixCaseOldAddress }}
                </template>
              </InputRadio>
              <InputRadio
                ref="newBillingType"
                v-model="billingAddressType"
                form-value="NEW"
                name="billing-address"
                class="c-input-radio mt-3"
                @input="analytics.click.newBillingType"
              >
                <template v-if="!showChoiceExperience">
                  {{ hrNewAddress }}
                </template>
                <template v-if="showChoiceExperience">
                  {{ mixCaseNewAddress }}
                </template>
              </InputRadio>
              <InputRadio
                v-if="showThirdAddressOption"
                ref="otherBillingType"
                v-model="billingAddressType"
                form-value="OTHER"
                name="billing-address"
                class="c-input-radio mt-3"
                @input="analytics.click.otherBillingType"
              >
                Enter a different address
              </InputRadio>
            </fieldset>
          </div>
          <div class="w-1/2 pl-xl">
            <h3 class="font-bold">
              More Information
            </h3>
            <p class="mt-4">
              Your card's billing address must match either the old or new address you entered earlier during this process.
              <template v-if="showThirdAddressOption">
                If your card is billed to a different address, you can enter it by selecting "Enter a different address."
              </template>
            </p>
            <p
              ref="moreInfoIcoaAmount"
              class="mt-4"
            >
              For your security, we need to verify your identity using a valid credit or debit card. You will be charged {{ icoaAmountFormatted }} for this service.
            </p>
            <p class="mt-4">
              We accept these cards:
            </p>
            <div class="flex mt-4">
              <img
                :src="$options.AmericanExpressSVG"
                alt="American Express"
                height="32px"
                width="45px"
              >
              <img
                :src="$options.DiscoverSVG"
                alt="Discover"
                class="ml-1"
                height="32px"
                width="45px"
              >
              <img
                :src="$options.VisaSVG"
                alt="VISA"
                class="ml-1"
                height="32px"
                width="45px"
              >
              <img
                :src="$options.MastercardSVG"
                alt="Mastercard"
                class="ml-1"
                height="32px"
                width="45px"
              >
            </div>
          </div>
        </div>
      </section>
      <div class="c-hairline mt-8" />
      <!-- 3rd Address Field "Other Billing Address" -->
      <template v-if="billingAddressType === 'OTHER'">
        <section
          ref="billingAddressFields"
          class="flex mt-8"
        >
          <div class="w-1/2 max:pr-xl">
            <h3 class="font-bold">
              Enter a different address
            </h3>
            <p
              v-if="informedDelivery"
              ref="informedDeliveryCopy"
              class="mt-5"
            >
              For security reasons Informed Delivery is currently not available for customers who are registering in conjunction with a third party address. Please visit
              <a
                href="https://informeddelivery.usps.com/box/pages/intro/start.action"
                rel="noopener noreferrer"
                target="_blank"
                class="c-link c-link--inline"
              >informeddelivery.usps.com</a> for alternative registration options.
            </p>
            <InputZipCode
              ref="billingZipCode"
              v-model="billingZipCode"
              label="ZIP Code™"
              placeholder="00000"
              name="billing-zip-code"
              class="mt-5"
              :has-error="errors.billingZipCode.hasError"
              :async-fn="zipSearch({
                cityName: billingCity,
                state: billingState,
              })"
              :transform-compare="zipCompare"
              :transform-label="zipLabel"
              :transform-value="zipValue"
              @select="billingCity = $event.cityName; billingState = $event.state;"
            >
              <template slot="helper">
                {{ errors.billingZipCode.message }}
              </template>
            </InputZipCode>
            <InputListBox
              ref="billingCity"
              v-model="billingCity"
              label="City"
              name="billing-city"
              placeholder="City"
              class="mt-5"
              :has-error="errors.billingCity.hasError"
              :async-fn="citySearch({
                state: billingState,
                zip: billingZipCode,
              })"
              :transform-compare="zipCompare"
              :transform-label="cityLabel"
              :transform-value="cityValue"
              @select="billingState = $event.state; billingZipCode = $event.zip;"
              @input="billingAddressError = ''; clearErrors('billingcity')"
            >
              {{ errors.billingCity.message }}
            </InputListBox>
            <InputState
              ref="billingState"
              v-model="billingState"
              label="State"
              name="billing-state"
              class="mt-5"
              :has-error="errors.billingState.hasError"
            >
              <template #helper>
                {{ errors.billingState.message }}
              </template>
            </InputState>
            <InputListBox
              ref="billingStreetAddress"
              v-model="billingStreetAddress"
              label="Street Address"
              name="billing-street-address"
              placeholder="12345 Main Street"
              class="mt-5"
              :has-error="errors.billingStreetAddress.hasError"
              :async-fn="streetSearch({
                cityName: billingCity,
                state: billingState,
                zipcode: billingZipCode,
              })"
              :transform-compare="streetCompare"
              :transform-label="streetLabel"
              :transform-value="streetValue"
              @input="billingPoBoxNumber = ''; billingPrivateMailbox = ''; billingAddressError = ''; clearErrors('billingstreetAddress')"
              @select="billingCity = $event.cityName;
                       billingState = $event.state;
                       billingZipCode = $event.zip"
            >
              {{ errors.billingStreetAddress.message }}
            </InputListBox>
            <InputText
              v-if="billingState === 'PR'"
              v-model="billingUrbanizationName"
              label="Urbanization Name (Puerto Rico only)"
              name="billing-urbanization-name"
              placeholder="URB HIGHLAND GDNS"
              class="mt-5"
              :has-error="errors.billingUrbanizationName.hasError"
            >
              <template #helper>
                {{ errors.billingUrbanizationName.message }}
              </template>
            </InputText>
            <InputText
              v-if="showBillingPoBox"
              ref="billingPoBox"
              v-model="billingPoBoxNumber"
              label="Po Box"
              name="billing-po-box"
              placeholder="PO Box 123"
              class="mt-5"
              :has-error="errors.billingPoBoxNumber.hasError"
              @input="billingAddressError = '';"
            >
              <template #helper>
                {{ errors.billingPoBoxNumber.message }}
              </template>
            </InputText>
            <InputText
              v-if="showBillingPrivateMailbox"
              v-model="billingPrivateMailbox"
              label="Private Mailbox"
              name="billing-private-mailbox"
              placeholder="PMB 123"
              class="mt-5"
              :has-error="errors.billingPrivateMailbox.hasError"
            >
              <template #helper>
                {{ errors.billingPrivateMailbox.message }}
              </template>
            </InputText>
          </div>
          <div class="w-1/2 pl-xl">
            <p>
              This is the billing address for your credit or debit card. Enter a valid address, and remember to include apartment/suite number if applicable.
            </p>
            <p class="mt-4">
              If your address is a PO Box, enter it in the Street Address field for example, PO Box 1001.
            </p>
            <p class="mt-4">
              Note: A Change-of-Address confirmation will be mailed to the old address listed above as notification of this mail forwarding request.
            </p>
            <p class="mt-4">
              We'll ask for the rest of your credit or debit card information on the next page.
            </p>
            <p
              v-if="billingPoBoxException && showBillingPoBox"
              class="errors text-red-200 mt-4"
            >
              <span class="font-bold">Note:</span> The address you provided is not considered a proper mailing address within this ZIP Code™. A proper mailing address within this ZIP Code™ requires a PO Box address. Please input your PO Box number in the “PO Box” field and click "Next" to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
            </p>
          </div>
        </section>
        <div class="c-hairline mt-8" />
        <!-- Buffer start date section -->
        <template v-if="bufferStartDate || sdBuffered || sdBufferedAlwaysToggle">
          <section class="flex mt-8">
            <div class="w-1/2 max:pr-xl">
              <h3 class="font-bold">
                We had to change your forwarding start date {{ bufferedStartDate }}
              </h3>
              <p
                id="buffer-start-date-msg"
                ref="bufferMessage"
                class="mt-5 text-sm"
              >
                When using a billing address other than your new or old address to validate your identity, the forwarding date must be no sooner than {{ moverType === 'BUSINESS' ? '7 days' : '7 days' }} from today. We've modified your start date to the earliest date possible, but you can make it even later if you prefer.
              </p>
              <InputDatePicker
                ref="bufferDatepicker"
                v-model="bufferedStartDate"
                :min="minBufferedStartDate"
                :max="maxStartDate"
                :open-date="minBufferedStartDate"
                label="Mail Forwarding Date"
                name="buffered-start-date"
                desktop
                class="mt-5"
                :has-error="errors.bufferedStartDate.hasError"
              >
                <template #helper>
                  {{ errors.bufferedStartDate.message }}
                </template>
              </InputDatePicker>
            </div>
            <div class="w-1/2 pl-xl">
              <p>
                {{ getFormattedDate(minBufferedStartDate, 'MM/dd/yyyy') }} is the soonest we can start forwarding. If you choose a later start date, your end date will also be pushed back.
              </p>
              <p
                ref="holdMailNote"
                class="mt-5"
              >
                <span class="font-bold">
                  Note:
                </span>
                If you are moving within {{ moverType === 'BUSINESS' ? '30 days' : '7 days' }}, you can have your mail held at your post office for 3 to 30 days by <a
                  href="https://holdmail.usps.com/holdmail/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="c-link c-link--inline"
                >putting your mail on hold online</a> or visiting your local post office.
              </p>
            </div>
          </section>
          <div class="c-hairline mt-8" />
        </template>
        <!-- Preparer section -->
        <section
          ref="preparer"
          class="flex mt-8"
        >
          <div class="w-1/2">
            <fieldset>
              <legend class="sr-only">
                Are you changing your own address or helping someone else?
              </legend>
              <h3 class="font-bold">
                Are you changing your own address or helping someone else?
              </h3>
              <InputRadio
                ref="ownRadio"
                v-model="preparerType"
                form-value="MOVER"
                name="preparer-type"
                class="c-input-radio mt-6"
                @input="analytics.click.mover"
              >
                I'm changing my own address
              </InputRadio>
              <InputRadio
                ref="preparerRadio"
                v-model="preparerType"
                form-value="PREPARER"
                name="preparer-type"
                class="c-input-radio mt-3"
                @input="analytics.click.preparer"
              >
                I'm helping someone else
              </InputRadio>
            </fieldset>
            <!-- Hidden Form Field for Preparer Contact Info -->
            <div
              v-if="preparerType === 'PREPARER'"
              ref="preparerFields"
            >
              <p class="mt-6">
                <strong>Please enter your own name and contact information</strong>
                (not the mover's information). We need this information in case we need to reach you with any questions about this Change-of-Address request.
              </p>
              <InputText
                ref="preparerFirstName"
                v-model="preparerFirstName"
                label="First Name"
                placeholder="First Name"
                name="first-name"
                class="mt-5"
                :has-error="errors.preparerFirstName.hasError"
                @input="errors.preparerFirstName.hasError = false"
              >
                <template
                  v-if="errors.preparerFirstName.hasError"
                  #helper
                >
                  {{ errors.preparerFirstName.message }}
                </template>
              </InputText>
              <InputText
                ref="preparerMiddleName"
                v-model="preparerMiddleName"
                label="Middle Name or Initial"
                placeholder="Middle Name or Initial"
                name="middle-initial"
                class="mt-5"
                :has-error="errors.preparerMiddleName && errors.preparerMiddleName.hasError"
                @input="handlePreparerMiddleName"
              >
                <template
                  v-if="errors.preparerMiddleName && errors.preparerMiddleName.hasError"
                  #helper
                >
                  {{ errors.preparerMiddleName.message }}
                </template>
              </InputText>
              <InputText
                ref="preparerLastName"
                v-model="preparerLastName"
                label="Last Name"
                placeholder="Last Name"
                name="last-name"
                class="mt-5"
                :has-error="errors.preparerLastName.hasError"
                @input="errors.preparerLastName.hasError = false"
              >
                <template
                  v-if="errors.preparerLastName.hasError"
                  #helper
                >
                  {{ errors.preparerLastName.message }}
                </template>
              </InputText>
              <InputText
                ref="preparerEmail"
                v-model.trim="preparerEmail"
                label="Email Address"
                placeholder="Hello@mail.com"
                name="email"
                class="mt-5"
                :has-error="errors.preparerEmail.hasError"
                @input="errors.preparerEmail.hasError = false"
              >
                <template
                  v-if="errors.preparerEmail.hasError"
                  #helper
                >
                  {{ errors.preparerEmail.message }}
                </template>
              </InputText>
              <div class="flex mt-5">
                <div class="w-1/2 mr-3">
                  <InputPhone
                    ref="preparerPhoneNumber"
                    v-model="preparerPhoneNumber"
                    label="Phone Number"
                    placeholder="(000) 000-0000"
                    name="phone"
                    :has-error="errors.preparerPhoneNumber.hasError"
                    @input="errors.preparerPhoneNumber.hasError = false"
                  >
                    <template #helper>
                      {{ errors.preparerPhoneNumber.message }}
                    </template>
                  </InputPhone>
                </div>
                <div class="w-1/2 ml-3">
                  <InputDropdown
                    ref="preparerPhoneType"
                    v-model="preparerPhoneType"
                    label="Phone Type"
                    name="phone-type"
                    :has-error="errors.preparerPhoneType.hasError"
                  >
                    <option value="MOBILE">
                      Mobile
                    </option>
                    <option value="HOME">
                      Home
                    </option>
                    <template #helper>
                      {{ errors.preparerPhoneType.message }}
                    </template>
                  </InputDropdown>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div class="c-hairline mt-8" />
      </template>
      <transition
        name="fade"
        appear
      >
        <!-- Recaptcha v3 when flag enabled and challenge prompted -->
        <!--
          Recaptcha v3 is no longer the preferred method of fraud detection, and prevention.
          IVS Proofing should be primary defense.
          @deprecated: Recaptcha v2 is disabled by default.
          @since: release/v23.0.8.0.
        -->
        <!-- Recaptcha v2 when billing type is other and flag is off -->
        <section
          v-if="(flagRecaptchaEnabled && billingAddressType === 'OTHER') || renderV3Challenge"
          class="mt-8"
        >
          <p class="mb-4">
            For your security, clicking the box below confirms that this Change-of-Address form is being completed by a person, and not an automated query.
          </p>
          <vue-recaptcha
            v-if="renderV3Challenge"
            id="recaptcha-challenge"
            ref="recaptchaChallenge"
            :sitekey="getRecaptchaV2Key()"
            @click.native="analytics.click.recaptchaVerified"
            @verify="recaptchaChallengeToken=$event;"
            @expired="recaptchaChallengeToken='';"
          />
          <vue-recaptcha
            v-else
            id="recaptcha"
            ref="recaptcha"
            :sitekey="getRecaptchaV2Key()"
            @click.native="analytics.click.recaptchaVerified"
            @verify="recaptchaToken=$event;"
            @expired="recaptchaToken='';"
          />
          <transition
            appear
            name="fade"
          >
            <p
              v-if="!flagRecaptchaEnabled && errors.recaptchaToken && errors.recaptchaToken.hasError"
              class="c-input-text__helper c-input-helper errors"
            >
              {{ errors.recaptchaToken.message }}
            </p>
            <p
              v-if="(
                renderV3Challenge
                && errors.recaptchaChallengeToken
                && errors.recaptchaChallengeToken.hasError
              )"
              class="c-input-text__helper c-input-helper errors"
            >
              {{ errors.recaptchaChallengeToken.message }}
            </p>
          </transition>
        </section>
      </transition>
      <!-- Bottom Navigation buttons -->
      <section class="mt-8">
        <router-link
          :to="{ name: 'verify-info-read', query: $route.query }"
          replace
          class="c-btn--secondary"
          @click.native="analytics.click.back"
        >
          Back
        </router-link>
        <button
          ref="next"
          class="c-btn ml-6"
          @click="captureNavigation"
        >
          Next
        </button>
      </section>
      <PrivacyActStatement
        v-if="billingAddressType === 'OTHER'"
        class="mt-8 w-1/2"
      />
    </form>
    <!-- for mfa modal-->
    <TwoFactor
      v-if="showMfaModal"
      :phone-number="mobilePhone"
      @close="showMfaModal = false"
    />
  </main>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import {
  InputDatePicker,
  InputDropdown,
  InputListBox,
  InputPhone,
  InputRadio,
  InputState,
  InputText,
  InputZipCode,
  IndividualProgressBarWithSteps,
} from '@mgo/ui/lib/components';
import { mapErrors } from '@mgo/core/src/validations/utils';
import {
  addDays, parseISO, startOfDay,
} from 'date-fns';
import { isDesktop } from '../../utils/deviceType';
import { track } from '../../analytics/events';
import { desktopAnalytics } from './analytics';
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import identityCheckMixin from './mixin';
import InfoPanel from '../../components/info-panel.vue';
import rapidKey from '../../mixins/rapidKey';
import grecaptcha from '../../mixins/grecaptcha';
// SVG imports
import AmericanExpressSVG from '../../assets/images/_logo-american-express.svg';
import DiscoverSVG from '../../assets/images/_logo-discover.svg';
import MastercardSVG from '../../assets/images/_logo-mastercard.svg';
import VisaSVG from '../../assets/images/_logo-visa.svg';
import destinationManager from '../../mixins/destinationManager';
import IndividualProgressBar from '../../../../ui/lib/components/progress-bar/IndividualProgressBar.vue';
import PrivacyActStatement from '../../components/PrivacyActStatement.vue';
import TwoFactor from '../TwoFactor/index.vue';

export default {
  name: 'DesktopIdentityCheck',
  components: {
    InputDatePicker,
    InputDropdown,
    InputListBox,
    InputPhone,
    InputRadio,
    InputState,
    InputText,
    InputZipCode,
    IndividualProgressBarWithSteps,
    VueRecaptcha,
    IndividualProgressBar,
    InfoPanel,
    PrivacyActStatement,
    TwoFactor,
  },
  mixins: [
    addressViolations,
    datePicker,
    identityCheckMixin,
    rapidKey,
    grecaptcha,
  ],
  data() {
    return {
      recaptchaToken: '',
    };
  },
  computed: {
    isDesktop,
    current() {
      return !this.showChoiceExperience ? 3 : 4;
    },
    totalSteps() {
      return !this.showChoiceExperience ? 6 : 7;
    },
    progressStep() {
      return this.current / this.totalSteps;
    },
    sdBufferedAlwaysToggle() {
      return JSON.parse(sessionStorage.getItem('sdBufferedAlwaysToggle'));
    },
    analytics() {
      return desktopAnalytics();
    },
    errors() {
      const fieldsToValidate = [];

      if (this.billingAddressType === 'OTHER') {
        const billingAddressFields = [
          'billingStreetAddress',
          'billingCity',
          'billingState',
          'billingZipCode',
        ];
        fieldsToValidate.push(...billingAddressFields);
      }

      if (this.billingAddressType === 'OTHER' && this.flagRecaptchaEnabled) {
        fieldsToValidate.push('recaptchaToken');
      }

      if (this.renderV3Challenge) {
        fieldsToValidate.push('recaptchaChallengeToken');
      }

      if (this.showBillingPrivateMailbox) {
        fieldsToValidate.push('billingPrivateMailbox');
      }

      if (this.billingState === 'PR') {
        fieldsToValidate.push('billingUrbanizationName');
      }

      if (this.showBillingPoBox) {
        fieldsToValidate.push('billingPoBoxNumber');
      }

      if (this.bufferStartDate || this.sdBuffered || this.sdBufferedAlwaysToggle) {
        fieldsToValidate.push('bufferedStartDate');
      }

      if (this.preparerType === 'PREPARER') {
        const preparerFields = [
          'preparerFirstName',
          'preparerLastName',
          'preparerEmail',
          'preparerPhoneNumber',
          'preparerPhoneType',
        ];
        fieldsToValidate.push(...preparerFields);
      }

      this.fieldErrors;
      return { ...mapErrors(this, fieldsToValidate), ...this.improvedFieldErrors };
    },
    coacUrl() {
      return destinationManager.coacUrl;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(newVal) {
        if (newVal?.name === 'identity-check') {
          this.$store.dispatch('mfa/stopValidatingLink');
        }
      },
    },
  },
  created() {
    setTimeout(() => {
      if (this.bufferedStartDate) {
        const startDate = startOfDay(new Date(this.startDate));
        const bufferedStartDate = startOfDay(new Date(this.bufferedStartDate));
        const thirtyDaysFuture = addDays(parseISO(bufferedStartDate), 30);

        if (startDate < thirtyDaysFuture) {
          this.bufferedStartDate = thirtyDaysFuture;
        }
      }
    }, 2000);
  },
  methods: {
    async captureNavigation() {
      // eslint-disable-next-line no-unused-expressions
      this.analytics.click.next();
      const visitedGlobalPayment = JSON.parse(sessionStorage.getItem('visitedGlobalPayment'));

      if (this.sdBuffered && visitedGlobalPayment) {
        sessionStorage.setItem('sdBufferedAlwaysToggle', 'true');
      }
      sessionStorage.setItem('visitedGlobalPayment', 'true');
    },
    async validate() {
      this.billingAddressError = '';
      this.fieldErrors = [];
      this.lazy = false;
      await this.$nextTick();
      if (this.flagRecaptchaEnabled && !this.renderV3Challenge) {
        const action = this.billingAddressType === 'OTHER' ? 'identity_check_third' : 'identity_check';
        const success = await this.recaptchaV3Validate(action);
        this.recaptchaV3Success = success;
      }
      if (this.flagRecaptchaEnabled
        && !this.recaptchaV3Success
        && !this.renderV3Challenge
        && !this.recaptchaChallengeToken) {
        this.lazy = true;
        return this.recaptchaInvokeChallenge();
      }
      if (!this.formIsValid) return this.scrollToFirstError();
      return this.submit();
    },
    submitLogValidation() {
      return this.post(`${this.api}/mgo-api/api/v1/coa/${this.coaUUID}/log-validation`, {});
    },
    async submit() {
      if (this.$store.state.loading) {
        this.submitLogValidation();
        return;
      }

      if (!this.informedDeliveryEligible) {
        this.informedDelivery = false;
      }

      this.patchInformedDelivery();
      this.$store.commit('ENABLE_LOADER');

      const payload = {
        ...this.coa,
        billingAddressType: this.billingAddressType,
        informedDelivery: this.informedDelivery,
        startDate: this.bufferStartDate ? this.bufferedStartDate : this.startDate,
        sdBuffered: this.bufferStartDate,
        multipleAddresses: this.coa.multipleAddresses,
      };

      const { xhr } = this.coa.multipleAddresses
        ? await this.$coa.businessCoaUpdate(payload)
        : await this.$coa.coaUpdate(payload);

      this.attachFieldErrors({ xhr });

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      track('CoaUpdated',
        {
          dcoa: this.dcoa,
          forwardType: this.forwardType,
          informedDelivery: this.informedDelivery,
          moverType: this.moverType,
          startDate: this.startDate,
          stopDate: this.stopDate,
        });

      const billingPayload = {
        addressType: 'BILLING',
      };

      if (this.billingAddressType === 'OTHER') {
        Object.assign(billingPayload, {
          streetAddress: this.billingStreetAddress,
          city: this.billingCity,
          state: this.billingState,
          zipCode: this.billingZipCode,
          urbanizationName: this.billingUrbanizationName,
          privateMailbox: this.billingPrivateMailbox,
          poBoxNumber: this.billingPoBoxNumber,
        });
      } else {
        [
          'streetAddress',
          'city',
          'state',
          'zipCode',
          'urbanizationName',
          'privateMailbox',
          'poBoxNumber',
        ].forEach((field) => {
          billingPayload[field] = this[`${this.billingAddressType.toLowerCase()}Address`][field];
        });
      }
      const requests = [];
      const billingAddressHandle = this.coa.multipleAddresses
        ? this.$coa.businessBillingAddressSave(this.$coa.orderUuid, billingPayload)
        : this.$coa.addressSave(billingPayload);

      requests.push(billingAddressHandle);
      if (this.preparerType === 'PREPARER') {
        let personHandle;
        const personData = {
          personType: 'PREPARER',
          firstName: this.preparerFirstName,
          middleName: this.preparerMiddleName,
          lastName: this.preparerLastName,
          email: this.preparerEmail,
          emailConfirm: this.preparerEmail,
          [`${this.preparerPhoneType.toLowerCase()}Phone`]: this.preparerPhoneNumber,
          phoneType: this.preparerPhoneType,
        };
        if (this.coa.multipleAddresses) {
          personHandle = await this.$coa.businessPersonSave(personData);
        } else {
          personHandle = await this.$coa.personSave(personData);
        }

        track(this.preparerUUID ? 'PersonUpdated' : 'PersonCreated',
          {
            personType: 'PREPARER',
            phoneType: this.preparerPhoneType,
          });
        this.prepareFieldErrors(personHandle);
        requests.push(personHandle);
      }

      // delete preparer person
      if (this.preparerType === 'MOVER') {
        let prepareHandle;
        const personData = {
          personType: 'PREPARER',
          firstName: this.preparerFirstName,
          middleName: this.preparerMiddleName,
          lastName: this.preparerLastName,
          email: this.preparerEmail,
          emailConfirm: this.preparerEmail,
          [`${this.preparerPhoneType.toLowerCase()}Phone`]: this.preparerPhoneNumber,
          phoneType: this.preparerPhoneType,
        };

        if (this.coa.multipleAddresses) {
          prepareHandle = await this.$coa.businessPersonDelete(personData);
        } else {
          prepareHandle = await this.$coa.personDelete(personData);
        }
        requests.push(prepareHandle);
      }

      const [
        billingAddressResponse,
        // eslint-disable-next-line
        personResponse,
        preparerResponse,
      ] = await Promise.all(requests);

      this.billingAddressError = billingAddressResponse?.xhr?.exception || '';
      this.attachFieldErrors(billingAddressResponse, 'billing');
      this.attachFieldErrors(personResponse);
      this.attachFieldErrors(preparerResponse);

      await this.$nextTick();

      if (billingAddressResponse?.xhr?.exception === 'InvalidPrimaryPoBoxException' && !this.billingStreetHasPoBox) {
        this.errors.billingPoBoxNumber.hasError = this.errors.billingStreetAddress.hasError;
        this.errors.billingPoBoxNumber.message = this.errors.billingStreetAddress.message;
        this.errors.billingStreetAddress.hasError = false;
        this.errors.billingStreetAddress.message = '';
      }
      await this.$nextTick();

      if (this.billingAddressError) {
        this.defaultException(this.billingAddressError);
      }

      // If there are logged errors
      // prevent the next step
      if (this.billingAddressError || this.fieldErrors.length) {
        this.$store.commit('DISABLE_LOADER');
        this.scrollToFirstError();
        return;
      }

      if (this.flagEMFEnabled) {
        if (this.showChoiceExperience || this.showSimplifyExperience) {
          const ext = this.extensionSelected || 0;
          await this.$coa.extensionSave(ext, this.coa?.visitId);
        } else if (!this.showChoiceExperience) {
          await this.$coa.extensionSave(this.emf || 0, this.coa?.visitId);
        }
      }

      // log the amsStatusCodes on 200 responses for billing addresses
      if (billingAddressResponse?.addressType
        && this.isAmsMatch(billingAddressResponse?.amsStatusCode)) {
        this.billingStreetAddress = billingAddressResponse?.streetAddress;
        this.billingCity = billingAddressResponse?.city;
        this.billingState = billingAddressResponse?.state;
        this.billingZipCode = billingAddressResponse?.zipCode;
      }

      // Pass exception from response in case errors are cleared from a double submit
      this.next(billingAddressResponse?.xhr?.exception);
    },
    handlePreparerMiddleName() {
      if (this.errors.preparerMiddleName) {
        this.errors.preparerMiddleName.hasError = false;
      }
    },
  },

  AmericanExpressSVG,
  DiscoverSVG,
  MastercardSVG,
  VisaSVG,
};
</script>
